import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { Navbar } from './components/Navbar'
import { Footer } from './components/Footer'
import { ErrorBoundary } from './components/ErrorBoundary'
import { useTheme } from './useTheme'
import { useI18n } from './i18n'
import { useMe, useBilling } from './api/hooks'
import { PageSpinner } from './components/ui'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { Landing } from './pages/Landing'
import { Features } from './pages/Features'
import { Legal } from './pages/Legal'
import { Discover } from './pages/Discover'
import { Dashboard } from './pages/Dashboard'
import { HorsePage } from './pages/HorsePage'
import { PublicProfile } from './pages/PublicProfile'
import { Auth } from './pages/Auth'
import { MagicLink } from './pages/MagicLink'
import { Onboarding } from './pages/Onboarding'
import { Paywall } from './pages/Paywall'
import { Settings } from './pages/Settings'
import { FarmOnboarding } from './pages/FarmOnboarding'
import { FarmDashboard } from './pages/FarmDashboard'
import { FarmHorseDetail } from './pages/FarmHorseDetail'
import { FarmTickets } from './pages/FarmTickets'
import { FarmTasks } from './pages/FarmTasks'
import { FarmInvoices } from './pages/FarmInvoices'
import { FarmMembers } from './pages/FarmMembers'
import { MyFarmPage } from './pages/MyFarmPage'
import { InviteAccept } from './pages/InviteAccept'
import { HorseShareAccept } from './pages/HorseShareAccept'
import { AdminConsole } from './pages/AdminConsole'
import { BottomNav } from './components/BottomNav'

function ScrollToTop() {
  const { pathname } = useLocation()
  // Block body (not an arrow-return) so the effect returns undefined, never
  // whatever window.scrollTo() yields — a non-function return would be called
  // as a cleanup and throw "n is not a function" on the next navigation.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

/** Gate for the core app: must be onboarded and not trial-locked. */
function AppGate() {
  const { data: me, isLoading } = useMe()
  const { data: billing, isLoading: bLoading } = useBilling()
  if (isLoading || bLoading) return <PageSpinner />
  if (me && !me.onboarded) return <Navigate to="/onboarding" replace />
  if (billing?.locked) return <Navigate to="/billing" replace />
  return <Outlet />
}

export default function App() {
  const { theme, toggle } = useTheme()
  const { t } = useI18n()
  const { pathname } = useLocation()

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <Navbar theme={theme} onToggle={toggle} />
      <div className="flex-1">
        {/* A page-level crash shows a recoverable screen; nav stays usable and
            navigating (key={pathname}) remounts the boundary — no hard refresh. */}
        <ErrorBoundary key={pathname}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/privacy" element={<Legal doc="privacy" />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/h/:id" element={<PublicProfile />} />
          <Route path="/signin" element={<Auth initial="signin" />} />
          <Route path="/signup" element={<Auth initial="signup" />} />
          <Route path="/magic" element={<MagicLink />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/billing" element={<Paywall />} />
            {/* Farm (B2B) — own gating inside each page, not the horse-owner AppGate */}
            <Route path="/farm/onboarding" element={<FarmOnboarding />} />
            <Route path="/farm" element={<FarmDashboard />} />
            <Route path="/farm/horse/:id" element={<FarmHorseDetail />} />
            <Route path="/farm/tickets" element={<FarmTickets />} />
            <Route path="/farm/tasks" element={<FarmTasks />} />
            <Route path="/farm/invoices" element={<FarmInvoices />} />
            <Route path="/farm/people" element={<FarmMembers />} />
            <Route path="/my-farm" element={<MyFarmPage />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/horse-share/:token" element={<HorseShareAccept />} />
            {/* Support console — the page itself redirects non-admins; the API re-checks */}
            <Route path="/admin" element={<AdminConsole />} />
            <Route element={<AppGate />}>
              <Route path="/app" element={<Dashboard />} />
              <Route path="/app/horse/:id" element={<HorsePage />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
          </Route>

          <Route
            path="*"
            element={
              <main className="mx-auto max-w-3xl px-5 py-24 text-center">
                <h1 className="text-3xl font-bold">{t('notFound.title')}</h1>
                <a href="/" className="btn-primary mt-6">{t('notFound.back')}</a>
              </main>
            }
          />
        </Routes>
        </ErrorBoundary>
      </div>
      <Footer />
      {/* Phone-first: signed-in users get a thumb-reach tab bar on small screens */}
      <BottomNav />
    </div>
  )
}
